import { Socket } from "socket.io-client"
import { ModalityDataSource } from "../Types/ModalityDataSource"
import { PageType } from "../Types/PageType"
import { PersystSocketResponse } from "../Types/SocketResponse"
import { ModalityPageProperties, Page } from "./Page"
import { createHash } from "crypto"

export type ImagePageProperties = ModalityPageProperties & {
	height: number
}

export class ImagePage<Properties extends ImagePageProperties=ImagePageProperties> extends Page<ArrayBuffer, Properties> {
	readonly height: number = 0

	private _loading = false

	get loaded(): boolean {
		return this.modalityDataSources.every(dataSource => (
			this.data.get(dataSource.dataObjectId)?.get("image") !== undefined
		))
	}

	get loading(): boolean {
		return this._loading
	}

	public getType(): PageType {
		return PageType.IMAGE
	}

	public getSettingsHash(): string {
		// Sometimes Persyst pages have a habit of coming back with the wrong settings.
		// By hashing together the settings that we care about, we can make sure that 
		// We only accept incoming images with the current settings.
		return createHash("md5")
			.update(JSON.stringify(this.getRenderSettings()))
			.digest("hex") 
	}

	public load = () => {
		return new Promise<Page<ArrayBuffer>>((resolve, reject) => {
			if (this.loaded || this.loading || this.width === 0) {
				resolve(this)
				return
			}

			this._loading = true

			if (this.getDataQuerySocket === null) {
				return
			}

			const socket = this.getDataQuerySocket(this.socketId)

			const loadTimeout = setTimeout(() => {
				// Prevents the Promise from hanging indefinitely. Sometimes messages get dropped, but it's not a problem.
				// We can just re-request the data.
				this._loading = false 
				reject("Page load timed out.")
			}, 5000)

			const listener = (socketResponse: PersystSocketResponse) => {
				if (socketResponse.page_id === this.id && socketResponse.settings_hash === this.getSettingsHash()) {
					const dataObjectData = this.data.get(socketResponse.data_object_id)

					// Set default
					if (!dataObjectData) {
						this.data.set(socketResponse.data_object_id, new Map())
					}

					this.data.get(socketResponse.data_object_id)?.set("image", socketResponse.image)
					socket.off(this.socketEventName(), listener)
					this._loading = false
					resolve(this)
					clearTimeout(loadTimeout)
				}
			}

			socket.on(this.socketEventName(), listener)
			socket.on("disconnect", reject)

			// Creating a batch for all of the modalities
			const batch = new Map<number, ModalityDataSource[]>()

			this.modalityDataSources.forEach(dataSource => {
				const current = batch.get(dataSource.dataObjectId) ?? []
				batch.set(dataSource.dataObjectId, [...current, dataSource])
			})

			this.requestData(batch, socket)

		})
	}

	requestData(batchedDataSources: Map<number, ModalityDataSource[]>, socket: Socket) {
		// Image pages will always request data for the entire duration of the page.
		// This is because there are no memory or performance concerns for rendering images.

		if (this.getDataQuerySocket === null) {
			return
		}

		batchedDataSources.forEach((dataSources, dataObjectId) => {
			socket.emit(
				this.socketEventName(), 
				this.patientId, 
				dataObjectId, 
				this.id, 
				dataSources, 
				this.startTime, 
				this.endTime,
				this.width, 
				this.patientIsAdmitted,
			)
		})
	}

	protected getRenderSettings(): Record<string, any> {
		// Returns settings that would change the image that is returned
		// The same settings should result in the same image.
		return {
			height: this.height,
			width: this.width,
			modalities: this.modalityDataSources
		}
	}
}
